import { LiveDriverMap, type DriverPin } from "@the-drop/live-driver-map";
import type { DashboardActiveTransporter } from "../lib/dispensary-dashboard-api";
import type { LiveDriverPositions } from "../hooks/useDispensaryDriversLive";

type Props = {
  transporters: DashboardActiveTransporter[];
  /**
   * Live `driverId → { lat, lng }` map from `useDispensaryDriversLive`.
   * The drivers list (the "who") is API-fetched; this prop is the "where"
   * sourced from the dispensary socket. Drivers in `transporters` without a
   * key in `positions` simply don't render on the map — same UX as
   * `transporter-web`'s `DeliveryMapPanel`.
   */
  positions: LiveDriverPositions;
};

/** Default map center (Denver) when no transporter has a fix yet. */
const MAP_DEFAULT_CENTER = { lat: 39.7392, lng: -104.9903 };

/**
 * Live-driver map for the dispensary dashboard.
 *
 * Data flow (mirrors `transporter-web`'s map pipeline):
 *   API (`/runs/active` → `mapActiveRunsToTransporters`) → drivers list (no positions)
 *   socket (`driver.location` on `/ws/dispensary/:id` → `useDispensaryDriversLive`)
 *     → positions map keyed by `driverId`
 *   This component joins the two by `driverId` to produce `DriverPin[]`.
 *
 * Renders Google Maps via `LiveDriverMap` when **`VITE_GOOGLE_MAPS_API_KEY`**
 * is set + well-formed (starts with `AIza`); falls back to the legacy SVG
 * schematic otherwise so the dashboard stays functional in dev without a key.
 */
export function DispensaryMap({ transporters, positions }: Props) {
  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  // Inline the validation — same check as customer-web / transporter-web. Worth lifting to a shared
  // util eventually, but not in this PR.
  const mapsKeyLooksValid = mapsApiKey.length >= 30 && mapsApiKey.startsWith("AIza");

  const driverPins: DriverPin[] = transporters.flatMap((t) => {
    const pos = positions[t.driverId];
    if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lng)) return [];
    return [{ driverId: t.driverId, position: pos }];
  });

  if (mapsKeyLooksValid) {
    return (
      <LiveDriverMap
        apiKey={mapsApiKey}
        drivers={driverPins}
        fallbackCenter={MAP_DEFAULT_CENTER}
      />
    );
  }

  return <DispensaryMapSchematic pins={driverPins} />;
}

/**
 * SVG schematic fallback used when no valid Google Maps key is configured. Same visual the dashboard
 * shipped with before the relocation work; kept so dev environments without a key still render.
 */
function DispensaryMapSchematic({ pins }: { pins: DriverPin[] }) {
  const pts = pins.filter((p) => p.position != null) as Array<
    DriverPin & { position: { lat: number; lng: number } }
  >;
  const bounds =
    pts.length > 0
      ? {
          minX: Math.min(...pts.map((p) => p.position.lng)),
          maxX: Math.max(...pts.map((p) => p.position.lng)),
          minY: Math.min(...pts.map((p) => p.position.lat)),
          maxY: Math.max(...pts.map((p) => p.position.lat)),
        }
      : { minX: -105.1, maxX: -104.9, minY: 39.6, maxY: 39.8 };

  const pad = 0.02;
  const w = bounds.maxX - bounds.minX || 0.1;
  const h = bounds.maxY - bounds.minY || 0.1;

  const project = (lat: number, lng: number) => {
    const x = ((lng - bounds.minX + pad) / (w + pad * 2)) * 100;
    const y = 100 - ((lat - bounds.minY + pad) / (h + pad * 2)) * 100;
    return { x: Math.min(95, Math.max(5, x)), y: Math.min(95, Math.max(5, y)) };
  };

  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      <defs>
        <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#D8D8D4" strokeWidth="0.08" />
        </pattern>
      </defs>
      <rect width="100" height="100" fill="#E8E8E4" />
      <rect width="100" height="100" fill="url(#grid)" opacity="0.35" />
      {pts.map((t) => {
        const { x, y } = project(t.position.lat, t.position.lng);
        return (
          <circle key={t.driverId} cx={x} cy={y} r="1.8" fill="#111111" stroke="white" strokeWidth="0.4" />
        );
      })}
    </svg>
  );
}
